import { Component, computed, effect, ElementRef, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { LayoutService } from '@/core/services/layout/layout.service';
import { Subscription } from "rxjs";
import { SessionService } from '@/core/services/security/session.service';
import { SessionTokenLifetimeService } from '@/core/services/security/session-token-lifetime.service';
import { UserProfileModalService } from '@/core/services/security/user-profile-modal.service';
import { ChangeTenantModalService } from '@/components/security/change-tenant-modal/services/change-tenant-modal.service';
import { MessagesUtils } from '@/core/utils/layout/messages.utils';
import { GlobalEvents } from '@/events/global/global.events';

@Component({
    selector: '[app-menu-profile]',
    templateUrl: './menuprofile.component.html',
    styleUrls: ['./menuprofile.component.scss'],
    standalone: false,
    animations: [
        trigger('menu', [
            transition('void => inline', [style({ height: 0 }), animate('400ms cubic-bezier(0.86, 0, 0.07, 1)', style({ opacity: 1, height: '*' }))]),
            transition('inline => void', [animate('400ms cubic-bezier(0.86, 0, 0.07, 1)', style({ opacity: 0, height: '0' }))]),
            transition('void => overlay', [style({ opacity: 0, transform: 'scaleY(0.8)' }), animate('.12s cubic-bezier(0, 0, 0.2, 1)')]),
            transition('overlay => void', [animate('.1s linear', style({ opacity: 0 }))])
        ])
    ],
    host: {
        class: 'layout-menu-profile'
    }
})
export class MenuProfileComponent implements OnInit, OnDestroy {
    layoutService = inject(LayoutService);
    renderer = inject(Renderer2);
    el = inject(ElementRef);
    sessionService = inject(SessionService);
    sessionTokenLifetimeService = inject(SessionTokenLifetimeService);
    userProfileModalService = inject(UserProfileModalService);
    changeTenantModalService = inject(ChangeTenantModalService);
    messagesUtils = inject(MessagesUtils);
    private globalEvents = inject(GlobalEvents);

    nomeUsuario = 'Não Identificado';
    perfilNome = 'Não Identificado';
    sessionTimeLabel = '0:00';
    showSessionTimer = false;
    canChangeTenant = false;

    private sessionTimerSubscription?: Subscription;
    private sessionRestoredSubscription?: Subscription;
    private userProfileUpdatedSubscription?: Subscription;

    isHorizontal = computed(() => this.layoutService.isHorizontal() && this.layoutService.isDesktop());

    menuProfileActive = computed(() => this.layoutService.layoutState().menuProfileActive);

    menuProfilePosition = computed(() => this.layoutService.layoutConfig().menuProfilePosition);

    isTooltipDisabled = computed(() => !this.layoutService.isSlim());

    subscription!: Subscription;

    outsideClickListener: any;

    constructor() {
        this.subscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (this.isHorizontal() && this.menuProfileActive()) {
                this.layoutService.layoutState.update(value => ({ ...value, menuProfileActive: false }))
            }
        })

        effect(() => {
            if (this.isHorizontal() && this.menuProfileActive() && !this.outsideClickListener) {
                this.bindOutsideClickListener();
            }

            if (!this.menuProfileActive() && this.isHorizontal()) {
                this.unbindOutsideClickListener();
            }
        })
    }

    ngOnInit(): void {
        this.loadSessionInfo();
        this.updateSessionTimerState(this.sessionTokenLifetimeService.remainingSeconds$.value);
        this.sessionTimerSubscription = this.sessionTokenLifetimeService.remainingSeconds$.subscribe(
            (remainingSeconds) => this.updateSessionTimerState(remainingSeconds),
        );
        this.sessionRestoredSubscription = this.globalEvents.sessionRestored$.subscribe(() => {
            this.loadSessionInfo();
        });
        this.userProfileUpdatedSubscription = this.globalEvents.userProfileUpdated$.subscribe(() => {
            this.loadSessionInfo();
        });
    }

    private loadSessionInfo(): void {
        const session = this.sessionService.getInstance();
        this.nomeUsuario = session?.user?.nome || 'Não Identificado';
        this.perfilNome = session?.user?.perfis?.[0]?.nome || 'Não Identificado';
        this.canChangeTenant = this.changeTenantModalService.canChangeTenant();
    }

    private updateSessionTimerState(remainingSeconds: number): void {
        this.showSessionTimer = this.sessionTokenLifetimeService.shouldShowSessionTimer();
        this.sessionTimeLabel = this.sessionTokenLifetimeService.formatRemainingTime(remainingSeconds);
    }

    renewSession(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.sessionTokenLifetimeService.requestReauthentication();
    }

    bindOutsideClickListener() {
        if (this.isHorizontal()) {
            this.outsideClickListener = this.renderer.listen(document, 'click', (event: MouseEvent) => {
                if (this.menuProfileActive()) {
                    const isOutsideClicked = !(this.el.nativeElement.isSameNode(event.target) || this.el.nativeElement.contains(event.target))
                    if (isOutsideClicked) {
                        this.layoutService.layoutState.update(value => ({ ...value, menuProfileActive: false }))
                    }
                }
            })
        }
    }

    unbindOutsideClickListener() {
        if (this.outsideClickListener) {
            this.outsideClickListener();
            this.outsideClickListener = null;
        }
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.sessionTimerSubscription?.unsubscribe();
        this.sessionRestoredSubscription?.unsubscribe();
        this.userProfileUpdatedSubscription?.unsubscribe();
        this.unbindOutsideClickListener();
    }

    toggleMenu() {
        this.layoutService.onMenuProfileToggle();
    }

    toggleConfigSidebar() {
        let layoutState = { ... this.layoutService.layoutState() };

        if (this.layoutService.isSidebarActive()) {
            layoutState.overlayMenuActive = false;
            layoutState.overlaySubmenuActive = false;
            layoutState.staticMenuMobileActive = false;
            layoutState.menuHoverActive = false;
            layoutState.configSidebarVisible = false;
        }
        layoutState.configSidebarVisible = !layoutState.configSidebarVisible;
        this.layoutService.layoutState.set(layoutState);
    }

    openProfileOnAction(): void {
        void this.userProfileModalService.openLoggedUserProfile();
    }

    openChangeTenantOnAction(): void {
        void this.changeTenantModalService.openChangeTenant();
    }

    logoutOnAction() {
        this.sessionService.logout();
        this.messagesUtils.clear();
        setTimeout(() => {
            this.messagesUtils.showMessageErrorToast(
                "messages.error.userNotAuthenticated",
            );
        }, 300);
    }
}
